import { useEffect, useMemo, useState } from "react";

import useLanguage from "../../hooks/useLanguage";
import { tableLayout } from "./tableLayout";
import { pickMobileColumns } from "../../components/virtualTable/mobileColumns";
import useContent from "../../hooks/useContent";
import useToaster from "../../hooks/useToaster";
import useMediaQuery from "../../hooks/useMediaQuery";
import useUserData from "../../hooks/useUserData";
import CommonPage from "../../components/common-page";
import VirtualTable from "../../components/virtualTable";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import {
  Box,
  Button,
  IconButton,
  TablePagination,
  Tooltip,
} from "@mui/material";
import QueueSkeleton from "./skeleton";

const ACTION_VIDEO = 3;
const STATUS_FAILED = 3;

// On a phone, drop the ID + timestamp columns; Action, Status and the row
// actions are what matter and they fit.
const MOBILE_COLUMN_WIDTHS = { action: 112, status: 82, actions: 66 };

// Tab → server status filter. "all" omits the filter (newest across statuses).
// The filter runs server-side (queue.dao), so e.g. "failed" returns every
// failed job rather than whatever falls in the newest page.
const STATUS_TABS = [
  { value: "all", status: undefined },
  { value: "pending", status: "pending" },
  { value: "processing", status: "processing" },
  { value: "completed", status: "completed" },
  { value: "failed", status: "failed" },
];
const PAGE_SIZE = 500; // server fetch cap
const ROWS_PER_PAGE = 20; // client-side rows shown per page

export default function Queue() {
  const [
    {
      pages: { queue: text },
    },
  ] = useLanguage();
  const isMobile = useMediaQuery();
  const columns = isMobile
    ? pickMobileColumns(tableLayout, MOBILE_COLUMN_WIDTHS)
    : tableLayout;
  const { getQueue, getQueueSourceVideoUrl, retryQueueItem } = useContent();
  const { addToaster } = useToaster();
  const currentUser = useUserData();
  const [queueItems, setQueueItems] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [tab, setTab] = useState("all");
  const [page, setPage] = useState(0);
  const titleText = useMemo(
    () => (isMobile ? text.inPageTitle.short : text.inPageTitle.long),
    [isMobile]
  );

  const fetchQueue = async (tabValue = tab) => {
    setFetching(true);
    const t = STATUS_TABS.find((x) => x.value === tabValue);
    const response = await getQueue({ status: t?.status, pageSize: PAGE_SIZE });
    if (response) setQueueItems(response);
    setFetching(false);
    setInitialLoad(false);
  };

  const handleTab = (value) => {
    setTab(value);
    setPage(0);
    fetchQueue(value);
  };

  const refetchQueue = async () => {
    await fetchQueue();
    addToaster({
      message: text.refreshed,
      severity: "success",
    });
  };

  const handleDownloadSource = async (id) => {
    const response = await getQueueSourceVideoUrl(id);
    if (response?.url) {
      window.location.href = response.url;
    } else {
      addToaster({
        message: text.actions.downloadFailed,
        severity: "error",
      });
    }
  };

  const handleRetry = async (id) => {
    const response = await retryQueueItem(id);
    if (response?.newId) {
      addToaster({
        message: text.actions.retrySuccess,
        severity: "success",
      });
      await fetchQueue();
    } else {
      addToaster({
        message: text.actions.retryFailed,
        severity: "error",
      });
    }
  };

  useEffect(() => {
    fetchQueue();
  }, []);

  if (initialLoad && fetching) return <QueueSkeleton />;

  const isAdmin = currentUser?.role === "admin";

  // Client-side pagination (20/page) over the fetched list.
  const pageCount = Math.max(1, Math.ceil(queueItems.length / ROWS_PER_PAGE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedItems = queueItems.slice(
    safePage * ROWS_PER_PAGE,
    safePage * ROWS_PER_PAGE + ROWS_PER_PAGE
  );

  return (
    <CommonPage
      title={titleText}
      subtitle={text.subtitle}
      action={{
        label: text.refresh,
        icon: <RefreshRoundedIcon fontSize="small" />,
        onClick: refetchQueue,
        disabled: fetching,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
        {STATUS_TABS.map((t) => (
          <Button
            key={t.value}
            size="small"
            variant={tab === t.value ? "contained" : "outlined"}
            onClick={() => handleTab(t.value)}
          >
            {text.tabs[t.value]}
          </Button>
        ))}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, width: "100%" }}>
        <VirtualTable
          height="100%"
          columns={columns}
          data={pagedItems}
          actions={(row) => {
            if (
              !isAdmin ||
              row.action !== ACTION_VIDEO ||
              row.status !== STATUS_FAILED
            ) {
              return [];
            }
            return [
              <Tooltip
                key="download-t"
                title={text.actions.downloadSource}
                arrow
                placement="top"
              >
                <IconButton
                  key="download"
                  onClick={() => handleDownloadSource(row.id)}
                  color="primary"
                >
                  <DownloadRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>,
              <Tooltip
                key="retry-t"
                title={text.actions.retry}
                arrow
                placement="top"
              >
                <IconButton
                  key="retry"
                  onClick={() => handleRetry(row.id)}
                  color="warning"
                >
                  <ReplayRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>,
            ];
          }}
        />
      </Box>
      {queueItems.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={queueItems.length}
          page={safePage}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      )}
    </CommonPage>
  );
}
